/**
 * Algorithm Orchestration Module
 *
 * Orchestrates the full diagnostic pipeline for the 滑雪智能鞋垫逐弯复盘网站:
 *   AR-1 turn detection → per-turn AR-2..AR-5 diagnostics → AR-6 evaluation
 *   → AR-7 fall detection → COP trace → RunSession assembly.
 *
 * Pipeline overview / 流水线概览:
 *   1. AR-1: split frames into turns (detectTurns)
 *   2. Per turn: AR-2 classification, AR-3 outside pressure, AR-4 edging sync,
 *      AR-5 turn shape, COP trace, time series, AR-6 overall score + level
 *   3. AR-7: whole-run fall detection (detectFalls)
 *   4. Build A1Summary (8 basic metrics) and B1Summary (12 advanced metrics)
 *   5. Assemble RunSession with all turns, frames, pressure arrays, summaries
 *
 * Deterministic per PO-5.4 (no random numbers; start_time fixed at 0).
 * 按 PO-5.4 确定性实现（无随机数；start_time 固定为 0）。
 */

import type {
  SkiTickData,
  DualPressureArray,
  Turn,
  RunSession,
  CopPoint,
  A1Summary,
  B1Summary,
  FallEvent,
} from '../types/ski-data';
import {
  TurnDirection,
  TurnType,
  EvaluationLevel,
} from '../types/enums';
import { GRAVITY } from '../constants/algorithm-params';

import { detectTurns } from './turn-detection';
import type { DetectedTurn } from './turn-detection';
import { classifyTurn } from './turn-classification';
import type { TurnClassificationResult } from './turn-classification';
import { computeOutsidePressure } from './outside-pressure';
import type { OutsidePressureResult } from './outside-pressure';
import { computeEdgingSync } from './edging-sync';
import type { EdgingSyncResult } from './edging-sync';
import { computeTurnShape } from './turn-shape';
import type { TurnShapeResult } from './turn-shape';
import { mapScoreToLevel, computeOverallTurnScore } from './evaluation';
import { detectFalls } from './fall-detection';
import { calculateCombinedCop } from '../utils/cop-calculator';
import type { RunScript } from '../data/turn-script';

// ===== Constants =====

/** Combined method ID traceable to all sub-algorithms used per turn. */
const TURN_METHOD_ID =
  'pipeline:a1-turns-gyro-extrema+b1-turn-classification+' +
  'b1-pressure-index-comprehensive+b1-edging-sync-calculator+' +
  'b1-turn-quality+b1-evaluation-level';

/** Fixed session ID for the simulation run. */
const SESSION_ID = 'sim-run-001';

/** Simulated slope angle in degrees (constant for the simulation). */
const SIM_SLOPE_ANGLE_DEG = 15;

/** Sample interval in seconds (100 Hz → 0.01 s). */
const SAMPLE_INTERVAL_S = 0.01;

// ===== Pipeline Result =====

/**
 * Complete output of the diagnostic pipeline.
 *
 * Contains the assembled RunSession, all Turn objects, fall events, and the
 * raw per-turn sub-algorithm results for downstream inspection.
 */
export interface DiagnosticPipelineResult {
  run_session: RunSession;
  turns: Turn[];
  fall_events: FallEvent[];
  turn_classifications: TurnClassificationResult[];
  outside_pressure_results: OutsidePressureResult[];
  edging_sync_results: EdgingSyncResult[];
  turn_shape_results: TurnShapeResult[];
}

// ===== Helper: Per-Turn Time Series =====

/**
 * Build the per-turn time series (pressure, edge angles, gyro Z) by slicing
 * the frame array over [start, end] (inclusive).
 *
 * 通过切片帧数组 [start, end]（含端点）构建弯内时间序列
 * （压力、立刃角、陀螺仪 Z）。
 */
function buildTurnTimeSeries(
  frames: SkiTickData[],
  start: number,
  end: number
): {
  pressure_frames: number[];
  edge_angle_frames: { left: number[]; right: number[] };
  gyro_z_series: number[];
} {
  const lo = Math.max(0, start);
  const hi = Math.min(frames.length - 1, end);

  const pressure_frames: number[] = [];
  const edgeLeft: number[] = [];
  const edgeRight: number[] = [];
  const gyroZSeries: number[] = [];

  for (let i = lo; i <= hi; i++) {
    const f = frames[i];
    pressure_frames.push(f.pressure_left + f.pressure_right);
    edgeLeft.push(f.edge_angle_left);
    edgeRight.push(f.edge_angle_right);
    gyroZSeries.push(f.gyroscope.z);
  }

  return {
    pressure_frames,
    edge_angle_frames: { left: edgeLeft, right: edgeRight },
    gyro_z_series: gyroZSeries,
  };
}

/**
 * Build the COP trace for a turn by computing the combined (both-feet) COP
 * for each pressure-array frame in [start, end] (inclusive).
 *
 * 对 [start, end]（含端点）内的每个压力阵列帧计算双脚合成 COP，
 * 构建弯内 COP 轨迹。
 */
function buildCopTrace(
  pressureArrays: DualPressureArray[],
  start: number,
  end: number
): CopPoint[] {
  const lo = Math.max(0, start);
  const hi = Math.min(pressureArrays.length - 1, end);

  const trace: CopPoint[] = [];
  for (let i = lo; i <= hi; i++) {
    const arr = pressureArrays[i];
    const cop = calculateCombinedCop(arr.left, arr.right, arr.timestamp_ms);
    if (cop !== null) {
      trace.push(cop);
    }
  }
  return trace;
}

// ===== Helper: Turn Assembly =====

/**
 * Assemble a full Turn object from the detected turn boundary and all
 * per-turn diagnostic results.
 *
 * The Turn carries three field groups:
 *   - metadata (from detectedTurn)
 *   - diagnostic conclusions (from AR-2..AR-6 results)
 *   - time series (sliced from frames + pressureArrays)
 *
 * 由检测到的弯边界与各项诊断结果组装完整的 Turn 对象。
 *
 * @param turnIndex       1-based turn index / 弯序号（从 1 起）
 * @param detectedTurn    AR-1 detected turn boundary / AR-1 检测的弯边界
 * @param frames          full frame array / 完整帧数组
 * @param pressureArrays  full pressure-array sequence / 完整压力阵列序列
 * @param classification  AR-2 classification result / AR-2 分类结果
 * @param pressure        AR-3 outside pressure result / AR-3 外板压力结果
 * @param edgingSync      AR-4 edging sync result / AR-4 立刃同步结果
 * @param shape           AR-5 turn shape result / AR-5 弯形质量结果
 * @param overallScore    AR-6 overall turn score (0-100) / AR-6 弯综合评分
 * @param level           AR-6 evaluation level / AR-6 评估等级
 * @returns assembled Turn / 组装好的 Turn
 */
export function buildTurnFromDiagnostics(
  turnIndex: number,
  detectedTurn: DetectedTurn,
  frames: SkiTickData[],
  pressureArrays: DualPressureArray[],
  classification: TurnClassificationResult,
  pressure: OutsidePressureResult,
  edgingSync: EdgingSyncResult,
  shape: TurnShapeResult,
  overallScore: number,
  level: EvaluationLevel
): Turn {
  const start = detectedTurn.start_frame_index;
  const end = detectedTurn.end_frame_index;

  // Build time series from the frame range / 从帧范围构建时间序列
  const series = buildTurnTimeSeries(frames, start, end);

  // Build COP trace from the pressure arrays / 从压力阵列构建 COP 轨迹
  const cop_trace = buildCopTrace(pressureArrays, start, end);

  // Resolve input frame timestamps for traceability / 解析输入帧时间戳以供追溯
  const inputFrameStartMs =
    frames[start]?.timestamp_ms ?? detectedTurn.start_ms;
  const inputFrameEndMs =
    frames[end]?.timestamp_ms ?? detectedTurn.end_ms;
  const inputFrameCount = Math.max(0, end - start + 1);

  return {
    // --- Metadata group / 元数据组 ---
    turn_index: turnIndex,
    start_ms: detectedTurn.start_ms,
    end_ms: detectedTurn.end_ms,
    direction: detectedTurn.direction,
    duration_ms: detectedTurn.duration_ms,

    // --- Diagnostic conclusions / 诊断结论组 ---
    turn_type: classification.turn_type,
    edge_angle_avg: classification.mean_edge_angle,
    outside_pressure_ratio: pressure.outside_ratio_peak,
    edging_sync_score: edgingSync.score,
    turn_roundness: shape.roundness,
    turn_symmetry: shape.symmetry,
    overall_score: overallScore,
    level,
    duration_class: detectedTurn.duration_class,

    // --- Diagnostic metadata / 诊断元数据 ---
    method_id: TURN_METHOD_ID,
    input_frame_start_ms: inputFrameStartMs,
    input_frame_end_ms: inputFrameEndMs,
    input_frame_count: inputFrameCount,

    // --- Time series group / 时间序列组 ---
    pressure_frames: series.pressure_frames,
    edge_angle_frames: series.edge_angle_frames,
    cop_trace,
    gyro_z_series: series.gyro_z_series,
    outside_ratio_series: pressure.outside_ratio_series,
    peak_gyro_z: detectedTurn.peak_gyro_z,
  };
}

// ===== A1 Summary =====

/**
 * Compute the A1 basic 8-metric summary for the whole run.
 *
 *   total_distance  : integrate speed over time (Σ speed × 0.01s)
 *   total_duration  : frames.length / 100 (seconds)
 *   max_speed       : max of all frame.speed
 *   avg_speed       : mean of all frame.speed
 *   slope_angle     : 15° (constant for simulation)
 *   turn_count      : turns.length
 *   altitude_drop   : total_distance × sin(15°)
 *   max_g_force     : max resultant acceleration / 9.81
 *
 * 计算整趟 A1 基础八项指标。
 *
 * @param frames - full frame array / 完整帧数组
 * @param turns  - assembled turns (for turn_count) / 已组装的弯数组
 * @returns A1Summary / A1 基础八项指标
 */
export function computeA1Summary(
  frames: SkiTickData[],
  turns: Turn[]
): A1Summary {
  // Edge case: no frames / 边界情况：无帧
  if (frames.length === 0) {
    return {
      total_distance: 0,
      total_duration: 0,
      max_speed: 0,
      avg_speed: 0,
      slope_angle: SIM_SLOPE_ANGLE_DEG,
      turn_count: turns.length,
      altitude_drop: 0,
      max_g_force: 0,
    };
  }

  // Integrate speed over time and track max / speed / 对速度积分并追踪最大值/均值
  let totalDistance = 0;
  let maxSpeed = 0;
  let speedSum = 0;
  let maxResultantAccel = 0;

  for (const f of frames) {
    totalDistance += f.speed * SAMPLE_INTERVAL_S;
    if (f.speed > maxSpeed) maxSpeed = f.speed;
    speedSum += f.speed;

    // Resultant acceleration magnitude / 合成加速度模长
    const ax = f.acceleration.x;
    const ay = f.acceleration.y;
    const az = f.acceleration.z;
    const resultant = Math.sqrt(ax * ax + ay * ay + az * az);
    if (resultant > maxResultantAccel) maxResultantAccel = resultant;
  }

  const avgSpeed = speedSum / frames.length;
  const totalDuration = frames.length / 100; // 100 Hz → seconds
  const altitudeDrop = totalDistance * Math.sin(
    (SIM_SLOPE_ANGLE_DEG * Math.PI) / 180
  );
  const maxGForce = maxResultantAccel / GRAVITY;

  return {
    total_distance: totalDistance,
    total_duration: totalDuration,
    max_speed: maxSpeed,
    avg_speed: avgSpeed,
    slope_angle: SIM_SLOPE_ANGLE_DEG,
    turn_count: turns.length,
    altitude_drop: altitudeDrop,
    max_g_force: maxGForce,
  };
}

// ===== B1 Summary =====

/**
 * Compute the B1 advanced 12-metric summary for the whole run.
 *
 *   avg_edge_angle         : mean of all turns' edge_angle_avg
 *   max_edge_angle         : max across turns' edge_angle_avg
 *   edge_angle_stability   : 100 - mean(edge_change_rate) × 10, clamped [0, 100]
 *   edging_sync_rate       : mean of all turns' edging_sync_score
 *   avg_outside_pressure   : mean of all turns' outside_pressure_ratio
 *   pressure_transfer_time : mean of all turns' transfer_time_ms
 *   pressure_stability     : mean of all turns' stability_score
 *   left_right_balance     : 100 - |left - right| / total × 100
 *   carving_ratio          : count of Carving / total (0-1)
 *   turn_roundness_avg     : mean of all turns' turn_roundness
 *   turn_symmetry_avg      : mean of all turns' turn_symmetry
 *   fall_count             : fallCount
 *
 * 计算整趟 B1 高级十二项指标。
 *
 * @param turns            - assembled turns / 已组装的弯数组
 * @param classifications  - per-turn AR-2 results (for edge_change_rate)
 *                           各弯 AR-2 结果（提供立刃变化率）
 * @param pressureResults  - per-turn AR-3 results (for transfer_time_ms, stability_score)
 *                           各弯 AR-3 结果（提供转移时间、稳定性分）
 * @param fallCount        - number of fall events / 摔倒次数
 * @returns B1Summary / B1 高级十二项指标
 */
export function computeB1Summary(
  turns: Turn[],
  classifications: TurnClassificationResult[],
  pressureResults: OutsidePressureResult[],
  fallCount: number
): B1Summary {
  const turnCount = turns.length;

  // Edge case: no turns → zeroed advanced metrics (except fall_count)
  // 边界情况：无弯 → 高级指标归零（除 fall_count 外）
  if (turnCount === 0) {
    return {
      avg_edge_angle: 0,
      max_edge_angle: 0,
      edge_angle_stability: 0,
      edging_sync_rate: 0,
      avg_outside_pressure: 0,
      pressure_transfer_time: 0,
      pressure_stability: 0,
      left_right_balance: 0,
      carving_ratio: 0,
      turn_roundness_avg: 0,
      turn_symmetry_avg: 0,
      fall_count: fallCount,
    };
  }

  // --- Edge angle aggregates (from turns) / 立刃角聚合（来自 turns） ---
  let edgeAngleSum = 0;
  let maxEdgeAngle = 0;
  for (const t of turns) {
    edgeAngleSum += t.edge_angle_avg;
    if (t.edge_angle_avg > maxEdgeAngle) maxEdgeAngle = t.edge_angle_avg;
  }
  const avgEdgeAngle = edgeAngleSum / turnCount;
  const maxEdgeAngleFinal = maxEdgeAngle;

  // --- Edge angle stability (from classifications.edge_change_rate) ---
  // 立刃稳定性（来自 classifications.edge_change_rate）
  let edgeChangeRateSum = 0;
  const clsCount = Math.min(classifications.length, turnCount);
  for (let i = 0; i < clsCount; i++) {
    edgeChangeRateSum += classifications[i].edge_change_rate;
  }
  const meanEdgeChangeRate = clsCount > 0 ? edgeChangeRateSum / clsCount : 0;
  const edgeAngleStability = Math.max(
    0,
    Math.min(100, 100 - meanEdgeChangeRate * 10)
  );

  // --- Edging sync rate (from turns.edging_sync_score) ---
  // 立刃同步率（来自 turns.edging_sync_score）
  let edgingSyncSum = 0;
  for (const t of turns) {
    edgingSyncSum += t.edging_sync_score;
  }
  const edgingSyncRate = edgingSyncSum / turnCount;

  // --- Outside pressure (from turns.outside_pressure_ratio) ---
  // 外板占比（来自 turns.outside_pressure_ratio）
  let outsidePressureSum = 0;
  for (const t of turns) {
    outsidePressureSum += t.outside_pressure_ratio;
  }
  const avgOutsidePressure = outsidePressureSum / turnCount;

  // --- Pressure transfer time & stability (from pressureResults) ---
  // 压力转移时间与稳定性（来自 pressureResults）
  let transferTimeSum = 0;
  let stabilitySum = 0;
  const prCount = Math.min(pressureResults.length, turnCount);
  for (let i = 0; i < prCount; i++) {
    transferTimeSum += pressureResults[i].transfer_time_ms;
    stabilitySum += pressureResults[i].stability_score;
  }
  const pressureTransferTime = prCount > 0 ? transferTimeSum / prCount : 0;
  const pressureStability = prCount > 0 ? stabilitySum / prCount : 0;

  // --- Left/right balance / 左右平衡 ---
  let leftCount = 0;
  let rightCount = 0;
  for (const t of turns) {
    if (t.direction === TurnDirection.Left) leftCount++;
    else if (t.direction === TurnDirection.Right) rightCount++;
  }
  const leftRightBalance =
    100 - (Math.abs(leftCount - rightCount) / turnCount) * 100;

  // --- Carving ratio / 卡宾比例 ---
  let carvingCount = 0;
  for (const t of turns) {
    if (t.turn_type === TurnType.Carving) carvingCount++;
  }
  const carvingRatio = carvingCount / turnCount;

  // --- Roundness & symmetry averages (from turns) ---
  // 圆润度与对称度均值（来自 turns）
  let roundnessSum = 0;
  let symmetrySum = 0;
  for (const t of turns) {
    roundnessSum += t.turn_roundness;
    symmetrySum += t.turn_symmetry;
  }
  const turnRoundnessAvg = roundnessSum / turnCount;
  const turnSymmetryAvg = symmetrySum / turnCount;

  return {
    avg_edge_angle: avgEdgeAngle,
    max_edge_angle: maxEdgeAngleFinal,
    edge_angle_stability: edgeAngleStability,
    edging_sync_rate: edgingSyncRate,
    avg_outside_pressure: avgOutsidePressure,
    pressure_transfer_time: pressureTransferTime,
    pressure_stability: pressureStability,
    left_right_balance: leftRightBalance,
    carving_ratio: carvingRatio,
    turn_roundness_avg: turnRoundnessAvg,
    turn_symmetry_avg: turnSymmetryAvg,
    fall_count: fallCount,
  };
}

// ===== Main Pipeline =====

/**
 * Run the full diagnostic pipeline over a simulation run.
 *
 * Steps / 步骤:
 *   1. AR-1: detectTurns(frames) → turn boundaries
 *   2. Per turn: AR-2 classify, AR-3 outside pressure, AR-4 edging sync,
 *      AR-5 turn shape, COP trace, time series, AR-6 overall score + level
 *   3. AR-7: detectFalls(frames) for the whole run
 *   4. Compute A1Summary (8 basic metrics)
 *   5. Compute B1Summary (12 advanced metrics)
 *   6. Assemble RunSession
 *
 * Deterministic: start_time is fixed at 0, all sub-algorithms are pure.
 *
 * 对模拟滑行数据执行完整诊断流水线。
 *
 * @param frames          - SkiTickData frames (100 Hz) / 滑雪帧数据
 * @param pressureArrays  - dual-foot pressure arrays (one per frame) / 双脚压力阵列
 * @param script          - run script (provides sport_type, duration_ms, etc.) / 滑行脚本
 * @returns DiagnosticPipelineResult / 诊断流水线结果
 */
export function runDiagnosticPipeline(
  frames: SkiTickData[],
  pressureArrays: DualPressureArray[],
  script: RunScript
): DiagnosticPipelineResult {
  // ----- Step 1: AR-1 turn detection / 步骤 1：AR-1 弯检测 -----
  const detectionResult = detectTurns(frames);
  const detectedTurns: DetectedTurn[] = detectionResult.turns;

  // ----- Step 2: per-turn diagnostics / 步骤 2：逐弯诊断 -----
  const turns: Turn[] = [];
  const turn_classifications: TurnClassificationResult[] = [];
  const outside_pressure_results: OutsidePressureResult[] = [];
  const edging_sync_results: EdgingSyncResult[] = [];
  const turn_shape_results: TurnShapeResult[] = [];

  for (const detected of detectedTurns) {
    const start = detected.start_frame_index;
    const end = detected.end_frame_index;

    // AR-2: turn classification / AR-2：弯分类
    const classification = classifyTurn(frames, start, end);
    turn_classifications.push(classification);

    // AR-3: outside pressure index / AR-3：外板压力指数
    const pressure = computeOutsidePressure(frames, start, end);
    outside_pressure_results.push(pressure);

    // AR-4: edging sync rate / AR-4：立刃同步率
    const edgingSync = computeEdgingSync(frames, start, end);
    edging_sync_results.push(edgingSync);

    // AR-5: turn shape quality / AR-5：弯形质量
    const shape = computeTurnShape(frames, start, end);
    turn_shape_results.push(shape);

    // AR-6: overall turn score + level / AR-6：弯综合评分 + 等级
    // The classification sub-score is the winning (max) of the three
    // carving/edging/skidding scores — a measure of classification strength.
    // 分类子分为三分数的最大值（分类强度度量）。
    const classificationScore = Math.max(
      classification.carving_score,
      classification.edging_score,
      classification.skidding_score
    );

    const overallScore = computeOverallTurnScore({
      turnShapeScore: shape.score,
      edgingSyncScore: edgingSync.score,
      outsidePressureScore: pressure.score,
      turnClassificationScore: classificationScore,
    });
    const level = mapScoreToLevel(overallScore);

    // Assemble the Turn object / 组装 Turn 对象
    const turn = buildTurnFromDiagnostics(
      detected.turn_index,
      detected,
      frames,
      pressureArrays,
      classification,
      pressure,
      edgingSync,
      shape,
      overallScore,
      level
    );
    turns.push(turn);
  }

  // ----- Step 3: AR-7 fall detection (whole run) / 步骤 3：AR-7 摔倒检测 -----
  const fall_events: FallEvent[] = detectFalls(frames);

  // ----- Step 4: A1 summary / 步骤 4：A1 基础八项指标 -----
  const a1_summary = computeA1Summary(frames, turns);

  // ----- Step 5: B1 summary / 步骤 5：B1 高级十二项指标 -----
  const b1_summary = computeB1Summary(
    turns,
    turn_classifications,
    outside_pressure_results,
    fall_events.length
  );

  // ----- Step 6: assemble RunSession / 步骤 6：组装 RunSession -----
  // Overall run score = mean of all turn overall_scores (0 when no turns).
  // 趟级评分 = 各弯综合评分均值（无弯时为 0）。
  const overallRunScore =
    turns.length > 0
      ? turns.reduce((sum, t) => sum + t.overall_score, 0) / turns.length
      : 0;
  const runLevel = mapScoreToLevel(overallRunScore);

  const run_session: RunSession = {
    session_id: SESSION_ID,
    sport_type: script.sport_type,
    start_time: 0, // deterministic / 确定性：固定为 0
    duration_ms: script.duration_ms,
    frame_count: frames.length,
    sample_rate: 100,
    frames,
    pressure_arrays: pressureArrays,
    turns,
    overall_score: overallRunScore,
    level: runLevel,
    a1_summary,
    b1_summary,
  };

  return {
    run_session,
    turns,
    fall_events,
    turn_classifications,
    outside_pressure_results,
    edging_sync_results,
    turn_shape_results,
  };
}
